import { message } from 'antd';
import { config } from '../config';
import { ApiResponse, ApiStatus } from '../types';

/**
 * HTTP 请求工具类
 * 提供统一的请求处理、错误处理和 token 管理
 */
export class HttpService {
  private baseURL: string;
  private timeout: number;
  private isTokenExpiredHandling: boolean = false; // 防止重复处理token过期

  constructor() {
    this.baseURL = config.apiBaseUrl;
    this.timeout = config.apiTimeout;
  }

  /**
   * 获取访问令牌
   */
  private getAccessToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  /**
   * 处理令牌过期
   */
  private handleTokenExpired(): void {
    // 防止重复处理
    if (this.isTokenExpiredHandling) {
      return;
    }
    
    this.isTokenExpiredHandling = true;
    
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('userMenus');
    localStorage.removeItem('userPermissions');
    message.error('登录已过期，请重新登录');
    
    // 延迟跳转，确保消息能显示
    setTimeout(() => {
      window.location.href = '/login';
    }, 100);
  }

  /**
   * 处理业务状态码
   */
  private handleBusinessStatus(data: ApiResponse): void {
    switch (data.status) {
      case ApiStatus.LOGIN_EXPIRED:
        this.handleTokenExpired();
        break;
      case ApiStatus.INVALID_CREDENTIALS:
        message.error('用户名或密码错误');
        break;
      case ApiStatus.USER_DISABLED:
        message.error('用户已被禁用');
        break;
      case ApiStatus.USER_NOT_FOUND:
        message.error('用户不存在');
        break;
      case ApiStatus.INSUFFICIENT_PERMISSIONS:
        message.error('权限不足');
        break;
      default:
        message.error(data.msg || '操作失败');
    }
  }

  /**
   * 处理 HTTP 错误
   */
  private handleHttpError(error: any): void {
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      message.error('网络连接失败');
    } else if (error.message.includes('HTTP error')) {
      const status = parseInt(error.message.match(/\d+/)?.[0] || '0');
      switch (status) {
        case 401:
          this.handleTokenExpired();
          break;
        case 403:
          message.error('访问被拒绝');
          break;
        case 404:
          message.error('接口不存在');
          break;
        case 500:
          message.error('服务器内部错误');
          break;
        default:
          message.error(`请求失败: ${status}`);
      }
    } else {
      message.error(error.message || '请求失败');
    }
  }

  /**
   * 发送带认证的 HTTP 请求
   * @param url 请求路径
   * @param options 请求选项
   * @returns Promise<any>
   */
  async request(url: string, options: RequestInit = {}): Promise<any> {
    const token = this.getAccessToken();
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    return this.sendRequest(url, config);
  }

  /**
   * 发送不带认证的 HTTP 请求（用于登录等接口）
   * @param url 请求路径
   * @param options 请求选项
   * @returns Promise<any>
   */
  async requestWithoutAuth(url: string, options: RequestInit = {}): Promise<any> {
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    return this.sendRequest(url, config);
  }

  /**
   * 发送 HTTP 请求的核心方法
   * @param url 请求路径
   * @param options 请求选项
   * @returns Promise<any>
   */
  private async sendRequest(url: string, options: RequestInit = {}): Promise<any> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(`${this.baseURL}${url}`, {
        ...options,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: ApiResponse = await response.json();

      // 处理业务状态码
      if (data.status === ApiStatus.SUCCESS) {
        return data;
      }

      // 处理特殊状态码
      this.handleBusinessStatus(data);
      // 对于业务错误，直接返回数据而不抛出错误，让调用方决定如何处理
      return data;
    } catch (error: any) {
      clearTimeout(timeoutId);

      if (error.name === 'AbortError') {
        message.error('请求超时');
      } else {
        this.handleHttpError(error);
      }

      throw error;
    }
  }

  /**
   * GET 请求
   * @param url 请求路径
   * @param params 查询参数
   * @returns Promise<any>
   */
  async get(url: string, params?: Record<string, any>): Promise<any> {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.request(`${url}${queryString}`, { method: 'GET' });
  }

  /**
   * POST 请求
   * @param url 请求路径
   * @param data 请求数据
   * @returns Promise<any>
   */
  async post(url: string, data?: any): Promise<any> {
    return this.request(url, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * PUT 请求
   * @param url 请求路径
   * @param data 请求数据
   * @returns Promise<any>
   */
  async put(url: string, data?: any): Promise<any> {
    return this.request(url, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * DELETE 请求
   * @param url 请求路径
   * @returns Promise<any>
   */
  async delete(url: string): Promise<any> {
    return this.request(url, { method: 'DELETE' });
  }

  /**
   * 不带认证的 GET 请求
   * @param url 请求路径
   * @param params 查询参数
   * @returns Promise<any>
   */
  async getWithoutAuth(url: string, params?: Record<string, any>): Promise<any> {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.requestWithoutAuth(`${url}${queryString}`, { method: 'GET' });
  }

  /**
   * 不带认证的 POST 请求
   * @param url 请求路径
   * @param data 请求数据
   * @returns Promise<any>
   */
  async postWithoutAuth(url: string, data?: any): Promise<any> {
    return this.requestWithoutAuth(url, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }
}

// 创建默认实例
export const httpService = new HttpService();

// 导出默认实例
export default httpService; 